<template>
  <FormControl :title="props.title" :sub-title="props.subTitle">
    <PairingGroup
      :id="id"
      :display="display"
      :options-name="optionsName"
      :matches-name="matchesName"
      :options="shuffledOptions"
      :matches="shuffledMatches"
      :is-preview-mode="true">
      <template #option="{ item }">
        <PairingItem
          :id="`${id}-${item.id}`"
          :format="item.format"
          :title="item.title"
          :image="item.image"
          :color="getColor(item.id, 'option')"
          :matches="item.matches"
          :is-readonly="true"
          :active="isActive(item.id)"
          @click="setActiveItem(item.id, 'option')">
          <template
            #image="{
              image,
              format,
              color,
            }: {
              image: string;
              format: PairingItemFormat;
              color: string;
            }">
            <PairingItemImage
              :image="image"
              :format="format"
              :color="color ?? '#F3F7FC'"/>
          </template>
          <template
            #title="{
              title,
              format,
              color,
            }: {
              title: string;
              format: PairingItemFormat;
              color: string;
            }">
            <PairingItemTitle
              :id="`${id}-${item.id}`"
              :title="title"
              :format="format"
              :rtl="props.rtl"
              :color="color || '#F3F7FC'"/>
          </template>
        </PairingItem>
      </template>
      <template #match="{ item }">
        <PairingItem
          :id="`${id}-${item.id}`"
          :format="item.format"
          :title="item.title"
          :image="item.image"
          :color="getColor(item.id, 'match')"
          :is-readonly="true"
          :active="isActive(item.id)"
          @click="setActiveItem(item.id, 'match')">
          <template
            #image="{
              image,
              format,
              color,
            }: {
              image: string;
              format: PairingItemFormat;
              color: string;
            }">
            <PairingItemImage
              :image="image"
              :format="format"
              :color="color ?? '#F3F7FC'"/>
          </template>
          <template
            #title="{
              title,
              format,
              color,
            }: {
              title: string;
              format: PairingItemFormat;
              color: string;
            }">
            <PairingItemTitle
              :id="`${id}-${item.id}`"
              :title="title"
              :format="format"
              :rtl="props.rtl"
              :color="color || '#F3F7FC'"/>
          </template>
        </PairingItem>
      </template>
    </PairingGroup>
  </FormControl>
</template>

<script setup lang="ts">
import FormControl from '@/components/utils/FormControl/FormControl.vue';
import { PairingGroup } from '@amit/components';
import { PairingItem } from '@amit/components';
import PairingItemTitle from './PairingItemTitle.vue';
import PairingItemImage from './PairingItemImage.vue';
import {
  PairingGroupDisplay,
  PairingItemData,
  PairingItemFormat,
} from '@amit/types';
import { useVModel } from '@vueuse/core';
import { shuffle } from 'lodash';
import { computed, onMounted, PropType, ref, watch } from 'vue';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  title: {
    type: String,
    default: '',
  },
  subTitle: {
    type: String,
    default: '',
  },
  display: {
    type: String as PropType<PairingGroupDisplay>,
    default: PairingGroupDisplay.Columns,
  },
  optionsName: {
    type: String,
    default: '',
  },
  matchesName: {
    type: String,
    default: '',
  },
  options: {
    type: Array as PropType<Array<PairingItemData>>,
    required: true,
  },
  matches: {
    type: Array as PropType<Array<PairingItemData>>,
    required: true,
  },
  isPreviewMode: {
    type: Boolean,
    default: false,
  },
  rtl: {
    type: Boolean,
    default: true,
  },
  review: {
    type: Boolean,
    default: false,
  },
  modelValue: {
    type: Object as PropType<Record<string, string[]>>,
    default: () => ({}),
  },
});

const pairingColors = [
  '#4B91FB',
  '#FBC02D',
  '#FF66AB',
  '#5BE6E9',
  '#9267F4',
  '#CCDC18',
  '#DC97F3',
  '#775D8F',
  '#49DFA5',
  '#BB9C1B',
];

const model = useVModel(props, 'modelValue');
const shuffledOptions = computed(() => shuffle(props.options));
const shuffledMatches = computed(() => shuffle(props.matches));

const activeItem = ref<{ type: 'option' | 'match'; id: string } | null>(null);
const colorMap = ref(new Map<string, string>());
const usedColors = ref<string[]>([]);

const assignColor = (id: string): string | null => {
  for (const color of pairingColors) {
    if (!usedColors.value.includes(color)) {
      colorMap.value.set(id, color);
      usedColors.value.push(color);
      return color;
    }
  }
  return null;
};

const releaseColor = (optionId: string) => {
  const color = colorMap.value.get(optionId);
  if (color) {
    usedColors.value = usedColors.value.filter(c => c !== color);
    colorMap.value.delete(optionId);
  }
};

const isActive = (id: string): boolean =>
  activeItem.value?.id === id ||
  Object.entries(model.value).some(
    ([optId, matchIds]) => optId === id || (matchIds as string[]).includes(id),
  );

const setActiveItem = (id: string, type: 'option' | 'match') => {
  if (props.review) return;
  const removePair = () => {
    if (type === 'match') {
      const existingPair = Object.entries(model.value).find(([, matchIds]) =>
        (matchIds as string[]).includes(id),
      );
      if (existingPair) {
        const [optionId] = existingPair;
        delete model.value[optionId];
        releaseColor(optionId);
        activeItem.value = null;
        return true;
      }
    } else if (type === 'option' && model.value[id]) {
      delete model.value[id];
      releaseColor(id);
      if (activeItem.value?.id === id) activeItem.value = null;
      return true;
    }
    return false;
  };

  if (removePair()) return;

  if (!activeItem.value) {
    activeItem.value = { type, id };
  } else if (activeItem.value.type !== type) {
    const { id: firstId, type: firstType } = activeItem.value;
    if (firstType === 'match') {
      model.value[id] = [firstId];
      assignColor(id);
    } else {
      model.value[firstId] = [id];
      assignColor(firstId);
    }
    activeItem.value = null;
  } else {
    activeItem.value = { type, id };
  }
};

const getColor = (id: string, type: 'option' | 'match'): string => {
  if (colorMap.value.has(id)) return colorMap.value.get(id)!;

  if (type === 'match') {
    const pair = Object.entries(model.value).find(([, matchIds]) =>
      (matchIds as string[]).includes(id),
    );
    const optionId = pair?.[0];
    if (optionId) return getColor(optionId, 'option');
  }

  if (activeItem.value?.id === id && activeItem.value.type === type) {
    return pairingColors.find(c => !usedColors.value.includes(c)) ?? '';
  }

  return '';
};

// Initialize colors for existing pairs
const initializeColors = () => {
  // Clear existing colors
  colorMap.value.clear();
  usedColors.value = [];

  // Assign colors to existing pairs
  Object.entries(model.value).forEach(([optionId, matchIds]) => {
    const color = assignColor(optionId);
    if (color) {
      // Also assign the same color to the matching item
      (matchIds as string[]).forEach(matchId => {
        colorMap.value.set(matchId, color);
      });
    }
  });
};

onMounted(() => {
  initializeColors();
});

// Re-initialize colors when model changes
watch(
  () => model.value,
  newVal => {
    if (!Object.keys(newVal).length) {
      activeItem.value = null;
      colorMap.value.clear();
      usedColors.value = [];
    } else if (
      colorMap.value.size === 0 ||
      Object.keys(newVal).length !== usedColors.value.length
    ) {
      initializeColors();
    }
  },
  { deep: true },
);
</script>
